import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import M365RecycleBin from "./M365RecycleBin";

interface Site {
  id: string;
  name?: string;
  display_name?: string;
  web_url?: string;
}
interface Drive {
  id: string;
  name?: string;
  drive_type?: string;
  web_url?: string;
}
interface DriveItem {
  id: string;
  name?: string;
  folder: boolean;
  size?: number;
  web_url?: string;
  last_modified?: string;
}
interface Crumb {
  id: string;
  name: string;
}

/**
 * Native SharePoint browser (read-only). Scoped by the global customer switcher
 * (no local picker). Search/list a tenant's sites, drill into libraries, then
 * browse folders/files with a breadcrumb. Backed by
 * /api/v1/m365/{customer_id}/sharepoint/{sites,sites/{id}/drives,drives/{id}/items}.
 */
export default function M365SharePointPanel({ customerId }: { customerId: number | null }) {
  const [search, setSearch] = useState("");
  const [sites, setSites] = useState<Site[]>([]);
  const [site, setSite] = useState<Site | null>(null);
  const [drives, setDrives] = useState<Drive[]>([]);
  const [drive, setDrive] = useState<Drive | null>(null);
  const [items, setItems] = useState<DriveItem[]>([]);
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sitesSortDir, setSitesSortDir] = useState<"asc" | "desc">("asc");
  const [showRecycle, setShowRecycle] = useState(false);

  const reset = () => {
    setSites([]);
    setSite(null);
    setDrives([]);
    setDrive(null);
    setItems([]);
    setCrumbs([]);
    setShowRecycle(false);
  };

  const loadSites = useCallback(() => {
    reset();
    if (customerId == null) return;
    setLoading(true);
    setError("");
    api
      .get(`/api/v1/m365/${customerId}/sharepoint/sites`, { params: { search } })
      .then((r) => setSites(r.data))
      .catch((e: unknown) => {
        setSites([]);
        const err = e as { response?: { data?: { detail?: string } } };
        setError(
          err.response?.data?.detail ||
            "Couldn't load sites (is M365 configured for this customer?)",
        );
      })
      .finally(() => setLoading(false));
  }, [customerId, search]);

  useEffect(() => {
    if (customerId != null) loadSites();
    else reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const openSite = (s: Site) => {
    setSite(s);
    setDrive(null);
    setItems([]);
    setCrumbs([]);
    setShowRecycle(false);
    setError("");
    api
      .get(`/api/v1/m365/${customerId}/sharepoint/sites/${s.id}/drives`)
      .then((r) => setDrives(r.data))
      .catch((e: unknown) => {
        setDrives([]);
        const err = e as { response?: { data?: { detail?: string } } };
        setError(err.response?.data?.detail || "Couldn't load libraries");
      });
  };

  const loadItems = (d: Drive, itemId: string) => {
    api
      .get(`/api/v1/m365/${customerId}/sharepoint/drives/${d.id}/items`, {
        params: { item_id: itemId },
      })
      .then((r) => setItems(r.data))
      .catch((e: unknown) => {
        setItems([]);
        const err = e as { response?: { data?: { detail?: string } } };
        setError(err.response?.data?.detail || "Couldn't load items");
      });
  };

  const openDrive = (d: Drive) => {
    setDrive(d);
    setCrumbs([{ id: "root", name: d.name || "Root" }]);
    setError("");
    loadItems(d, "root");
  };

  const openFolder = (item: DriveItem) => {
    if (!drive) return;
    setCrumbs((c) => [...c, { id: item.id, name: item.name || "Folder" }]);
    loadItems(drive, item.id);
  };

  const goToCrumb = (idx: number) => {
    if (!drive) return;
    const next = crumbs.slice(0, idx + 1);
    setCrumbs(next);
    loadItems(drive, next[next.length - 1].id);
  };

  if (customerId == null) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 italic">
        Select a customer in the header to browse SharePoint.
      </p>
    );
  }

  const sortedSites = [...sites].sort((a, b) => {
    const av = (a.display_name || a.name || a.id).toLowerCase();
    const bv = (b.display_name || b.name || b.id).toLowerCase();
    return sitesSortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Search sites…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") loadSites();
          }}
          className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-56"
        />
        <button
          onClick={loadSites}
          className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700"
        >
          Search
        </button>
      </div>

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mb-3">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading sites…</p>
      ) : !site ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm divide-y divide-gray-100 dark:divide-gray-700">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <button
              onClick={() => setSitesSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-gray-100"
              title="Sort by name"
            >
              Site <span className="text-gray-400 dark:text-gray-500">{sitesSortDir === "asc" ? "▲" : "▼"}</span>
            </button>
            <span className="text-xs text-gray-400 dark:text-gray-500">{sites.length}</span>
          </div>
          {sortedSites.length === 0 && (
            <p className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
              No sites found.
            </p>
          )}
          {sortedSites.map((s) => (
            <button
              key={s.id}
              onClick={() => openSite(s)}
              title={s.display_name || s.name || s.id}
              className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              <span className="text-sm text-gray-800 dark:text-gray-100">
                {s.display_name || s.name || s.id}
              </span>
              {s.web_url && (
                <a
                  href={s.web_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Open ↗
                </a>
              )}
            </button>
          ))}
        </div>
      ) : showRecycle ? (
        <div>
          <button
            onClick={() => setShowRecycle(false)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline mb-2"
          >
            ← Back to libraries
          </button>
          <div className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-2">
            {site.display_name || site.name} — recycle bin
          </div>
          {site.web_url ? (
            <M365RecycleBin
              customerId={customerId}
              scope={{ type: "sharepoint", siteUrl: site.web_url }}
            />
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic">
              This site has no web URL, so its recycle bin can't be resolved.
            </p>
          )}
        </div>
      ) : !drive ? (
        <div>
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => {
                setSite(null);
                setDrives([]);
              }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              ← Back to sites
            </button>
            <button
              onClick={() => setShowRecycle(true)}
              className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              🗑 Recycle bin
            </button>
          </div>
          <div className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-2">
            {site.display_name || site.name} — libraries
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-100">
            {drives.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-400 text-sm">No libraries.</p>
            )}
            {drives.map((d) => (
              <button
                key={d.id}
                onClick={() => openDrive(d)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50"
              >
                <span className="text-sm text-gray-800">
                  {d.name || d.id}
                  {d.drive_type && <span className="text-xs text-gray-400 ml-2">{d.drive_type}</span>}
                </span>
                {d.web_url && (
                  <a
                    href={d.web_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Open ↗
                  </a>
                )}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <button
            onClick={() => {
              setDrive(null);
              setItems([]);
              setCrumbs([]);
            }}
            className="text-xs text-blue-600 hover:underline mb-2"
          >
            ← Back to libraries
          </button>
          <div className="flex flex-wrap items-center gap-1 text-xs text-gray-500 mb-2">
            {crumbs.map((c, i) => (
              <span key={c.id} className="flex items-center gap-1">
                {i > 0 && <span className="text-gray-300">/</span>}
                <button onClick={() => goToCrumb(i)} className="hover:underline hover:text-blue-600">
                  {c.name}
                </button>
              </span>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-100">
            {items.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-400 text-sm">Empty folder.</p>
            )}
            {items.map((it) => (
              <div
                key={it.id}
                className="flex items-center justify-between px-4 py-2 hover:bg-gray-50"
              >
                {it.folder ? (
                  <button
                    onClick={() => openFolder(it)}
                    className="text-sm text-gray-800 text-left hover:underline"
                  >
                    📁 {it.name}
                  </button>
                ) : (
                  <span className="text-sm text-gray-700">📄 {it.name}</span>
                )}
                {it.web_url && (
                  <a
                    href={it.web_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Open ↗
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
